import React, { useEffect, useState } from 'react';
import { Table, Input, Button, Breadcrumb, Modal, message, DatePicker, Space, InputNumber, Row, Col, Form, Grid } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { EyeOutlined, DeleteOutlined, EditOutlined, FileExcelOutlined, FilePdfOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
// import { LoadableContent } from '../../components';
import { partenairesService } from '../../common/services';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Can from '../../components/can';
import { toast } from 'react-toastify';

const { RangePicker } = DatePicker;

const Partenaires = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [isLoading, setLoading] = useState(false);

  // Recherche + filtre dates
  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null); // [dayjs, dayjs]

  // Pagination/tri serveur
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-createdAt'); // string API

  const navigate = useNavigate();

  // Edition (inchangé)
  const [editingPartenaire, setEditingPartenaire] = useState(null);
  const [isEditModalVisible, setEditModalVisible] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;               // < md
  const controlSize = isMobile ? 'small' : 'middle';
  const btnFull = { width: isMobile ? '100%' : 'auto' };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let local = phone.replace('+225', '').trim();
    return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort,
        q: q || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const data = await partenairesService.getList(params);

      // Compat : si ancien backend renvoyait un tableau
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Erreur chargement partenaires :', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sort, q, dateRange]);

  // Export (exporte la page courante filtrée côté serveur)
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(items);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Partenaires");
    XLSX.writeFile(workbook, "partenaires.xlsx");
  };

  const exportToCSV = () => {
    const worksheet = XLSX.utils.json_to_sheet(items);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'partenaires.csv');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Liste des partenaires", 14, 10);

    autoTable(doc, {
      startY: 20,
      head: [['Nom', 'Prénom', 'Téléphone', 'Date de création', 'Points']],
      body: items.map((item) => [
        item.firstName,
        item.lastName,
        formatPhoneNumber(item.phone),
        item.createdAt, // déjà formaté DD/MM/YYYY HH:mm:ss par l'API
        Number(item?.soldePoints?.point ?? 0).toFixed(2)
      ]),
    });

    doc.save("partenaires.pdf");
  };

  const handleDelete = (partenaire) => {
    const { _id, firstName, lastName, phone } = partenaire;

    Modal.confirm({
      title: "Confirmer la suppression",
      content: (
        <>
          <p>Êtes-vous sûr de vouloir supprimer ce partenaire ?</p>
          <p><strong>Nom :</strong> {firstName} {lastName}</p>
          <p><strong>Téléphone :</strong> {formatPhoneNumber(phone)}</p>
        </>
      ),
      okText: "Oui, supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      width: isMobile ? 360 : 520,
      styles: {
        body: { padding: isMobile ? 12 : 24 },
      },
      okButtonProps: { danger: true, size: controlSize, style: btnFull },
      cancelButtonProps: { size: controlSize, style: btnFull },
      onOk: async () => {
        try {
          await partenairesService.supprimer(_id);
          message.success("Partenaire supprimé avec succès !");
          fetchList();
        } catch (error) {
          console.error("Erreur lors de la suppression :", error);
          message.error("Une erreur est survenue pendant la suppression.");
        }
      }
    });
  };


  // Colonnes (pas de filtres antd — on laisse la recherche globale côté serveur)
  const columns = [
    {
      title: 'Numéro de téléphone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => formatPhoneNumber(phone),
      sorter: true,
    },
    {
      title: 'Nom',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: true,
    },
    {
      title: 'Prénom(s)',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: true,
    },
    {
      title: 'OTP',
      dataIndex: 'resetPasswordOtp',
      key: 'resetPasswordOtp',
      // tri textuel si besoin (géré via "sort" -> "resetPasswordOtp")
      sorter: false, // tu peux le passer à true si tu veux exposer ce tri côté API
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
    },
    {
      title: 'Points',
      key: 'points',
      dataIndex: ['soldePoints', 'point'],
      render: (pt) => Number(pt ?? 0).toFixed(2),
      sorter: true, // mappe vers "points"
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/dashboard/partenaire/${record._id}`)}
          />
          <Can perm="partners:update">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setEditingPartenaire({
                  ...record,
                  currentPoints: Number(record?.soldePoints?.point ?? 0), // 👈 important
                  pointsToAdd: 0,                                         // delta saisi par l’admin
                });
                setEditModalVisible(true);
              }}

            />
          </Can>
          <Can perm="partners:delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
            />
          </Can>
        </div>
      ),
    },
  ];

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    // map AntD sorter -> API sort
    if (sorter?.field) {
      const field = sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';

      if (field === 'createdAt') setSort(`${dir}createdAt`);
      else if (field === 'firstName') setSort(`${dir}firstName`);
      else if (field === 'lastName') setSort(`${dir}lastName`);
      else if (field === 'phone') setSort(`${dir}phone`);
      else if (field === 'points') setSort(`${dir}points`); // mappé côté API -> soldePoints.point
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="row">
          <div className="col-md-12">
            <Breadcrumb
              style={{ marginBottom: 16 }}
              items={[
                { title: <Link to="/dashboard" style={{ fontWeight: 'bold' }}>🏠 ACCUEIL</Link> },
                { title: <span style={{ fontWeight: 'bold' }}>👥 TIGOON</span> },
              ]}
            />

            <div className="card mb-4">
              <div className="card-body">
                {/* Filtres globaux */}
                <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Rechercher (toutes colonnes)"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setPage(1); }}
                    style={{ width: 320 }}
                    allowClear
                  />

                  <RangePicker
                    onChange={(range) => { setDateRange(range); setPage(1); }}
                    placeholder={['Date début', 'Date fin']}
                    format="DD/MM/YYYY"
                    allowClear
                  />
                </div>

                {/* Export */}
                <div style={{ marginBottom: 16 }}>
                  {/* Space avec wrap + boutons plein largeur en mobile */}
                  <Space wrap size={[8, 8]} style={{ width: '100%' }}>
                    <Button
                      icon={<FileExcelOutlined />}
                      onClick={exportToExcel}
                      type="primary"
                      size={controlSize}
                      style={btnFull}
                    >
                      Exporter Excel (.xlsx)
                    </Button>

                    <Button
                      icon={<DownloadOutlined />}
                      onClick={exportToCSV}
                      type="default"
                      size={controlSize}
                      style={btnFull}
                    >
                      Exporter CSV
                    </Button>

                    <Button
                      icon={<FilePdfOutlined />}
                      onClick={exportToPDF}
                      danger
                      size={controlSize}
                      style={btnFull}
                    >
                      Exporter PDF
                    </Button>
                  </Space>
                </div>


                {/* Table serveur */}
                <Table
                  rowKey="_id"
                  columns={columns}
                  dataSource={items}
                  loading={isLoading}
                  onChange={onTableChange}
                  pagination={{
                    current: page,
                    pageSize: limit,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [5, 10, 20, 50, 100],
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal édition (inchangé) */}
      <Modal
        title="Modifier le partenaire"
        open={isEditModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={async () => {
          try {
            const payload = {
              firstName: editingPartenaire.firstName?.trim(),
              lastName: editingPartenaire.lastName?.trim(),
              phone: editingPartenaire.phone,
              // on envoie le DELTA uniquement
              pointsDelta: Number(editingPartenaire.pointsToAdd || 0),
            };
            await partenairesService.updatePartenaire(editingPartenaire._id, payload);
            message.success("Partenaire modifié avec succès !");
            setEditModalVisible(false);
            fetchList();
          } catch (error) {
            const errorMessage = error.message || 'Une erreur est survenue lors de la connexion.';
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 5000,
            });
            // console.error("Erreur lors de la mise à jour :", error);
            // message.error("Échec de la mise à jour du partenaire.");
          }
        }}
        okText="Enregistrer"
        cancelText="Annuler"
        centered
        maskClosable
        destroyOnClose
        width={isMobile ? 360 : 720}                 // largeur adaptée
        okButtonProps={{ size: controlSize, style: btnFull }}
        cancelButtonProps={{ size: controlSize, style: btnFull }}
        styles={{
          body: { padding: isMobile ? 12 : 24 },     // padding réduit en mobile
        }}
      >
        <Form layout="vertical" size={controlSize}>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12}>
              <Form.Item label="Nom">
                <Input
                  placeholder="Nom"
                  value={editingPartenaire?.firstName}
                  onChange={(e) =>
                    setEditingPartenaire(prev => ({ ...prev, firstName: e.target.value }))
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Prénom">
                <Input
                  placeholder="Prénom"
                  value={editingPartenaire?.lastName}
                  onChange={(e) =>
                    setEditingPartenaire(prev => ({ ...prev, lastName: e.target.value }))
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Numéro de téléphone">
                <Input
                  addonBefore="+225"
                  placeholder="Ex : 09040607"
                  maxLength={10}
                  value={editingPartenaire?.phone?.replace('+225', '') || ''}
                  onChange={(e) =>
                    setEditingPartenaire(prev => ({
                      ...prev,
                      phone: `+225${e.target.value.replace(/\s+/g, '')}`
                    }))
                  }
                />
              </Form.Item>
            </Col>

            {/* --- Points : solde actuel (grisé) --- */}
            <Col xs={24} md={12}>
              <Form.Item label="Solde de points (actuel)">
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  value={
                    typeof editingPartenaire?.currentPoints === 'number'
                      ? editingPartenaire.currentPoints
                      : Number(editingPartenaire?.soldePoints?.point ?? 0)
                  }
                  formatter={(v) => (v ?? 0).toLocaleString('fr-FR')}
                />
              </Form.Item>
            </Col>

            {/* --- Points : à ajouter (delta) --- */}
            <Col xs={24} md={12}>
              <Form.Item label="Points à ajouter (aujourd’hui)">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Ex : 50 (ajoute) ou -20 (retire)"
                  min={-1e12}
                  max={1e12}
                  step={0.01}
                  value={editingPartenaire?.pointsToAdd}
                  onChange={(v) =>
                    setEditingPartenaire(prev => ({
                      ...prev,
                      pointsToAdd: typeof v === 'number' ? v : 0
                    }))
                  }
                />
              </Form.Item>
            </Col>

            {/* --- Aperçu du nouveau solde --- */}
            <Col xs={24} md={12}>
              <Form.Item label="Nouveau solde (estimé)">
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  value={(
                    Number(editingPartenaire?.currentPoints ?? 0) +
                    Number(editingPartenaire?.pointsToAdd ?? 0)
                  )}
                  formatter={(v) => (v ?? 0).toLocaleString('fr-FR')}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>



    </div>
  );
};

export default Partenaires;
